import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => {
    // Initialize from localStorage
    const savedToken = localStorage.getItem('token');
    console.log('Initializing token from storage:', savedToken ? 'FOUND' : 'NOT FOUND');
    return savedToken;
  });

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      console.log('Token found, fetching user...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      console.log('No token found');
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      console.log('Fetching user data...');
      
      // Try to get cached user first
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          const parsedUser = JSON.parse(cachedUser);
          console.log('Using cached user:', parsedUser.role);
          setUser(parsedUser);
          setLoading(false);
          return;
        } catch (e) {
          console.log('Failed to parse cached user, fetching fresh...');
        }
      }
      
      // Fetch fresh user data
      const response = await axios.get(`${API_URL}/auth/me`);
      console.log('User fetched:', response.data);
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, full_name, phone, referral_code = '') => {
    try {
      const payload = {
        email,
        password,
        full_name,
        phone
      };
      
      // Only include referral_code if it's provided
      if (referral_code) {
        payload.referral_code = referral_code;
      }
      
      const response = await axios.post(`${API_URL}/auth/register`, payload);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Always redirect new users to dashboard (they will be regular users)
      window.location.href = '/dashboard';
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password
      });
      const { access_token, user: userData } = response.data;
      
      console.log('Login successful, user role:', userData.role);
      console.log('Saving token to localStorage...');
      
      // Save to localStorage FIRST
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      // Then update state
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      console.log('Token saved:', localStorage.getItem('token') ? 'YES' : 'NO');
      
      // Add delay to ensure localStorage is written
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Redirect based on role
      if (userData.role === 'admin') {
        console.log('Redirecting to admin dashboard...');
        window.location.href = '/admin';
      } else {
        console.log('Redirecting to user dashboard...');
        window.location.href = '/dashboard';
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error.response?.data);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed'
      };
    }
  };

  const logout = () => {
    console.log('Logging out...');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin'
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};